// src/components/Admin/CreateSuperuser.js  
import React, { useState, useEffect } from "react";  
import { motion, AnimatePresence } from "framer-motion";  
import { Shield, X, Eye, EyeOff, CheckCircle, AlertCircle, RefreshCw } from "lucide-react";  
import api from "../../api";  
  
/* Password strength: mirrors backend validate_password_strength intent */  
const evaluatePassword = (pw) => {  
  const checks = {  
    length: pw.length >= 8,  
    upper: /[A-Z]/.test(pw),  
    lower: /[a-z]/.test(pw),  
    number: /\d/.test(pw),  
    symbol: /[^A-Za-z0-9]/.test(pw),  
  };  
  const score = Object.values(checks).filter(Boolean).length;  
  const label = ["Too weak", "Weak", "Fair", "Good", "Strong", "Excellent"][score] || "Too weak";  
  const tone =  
    score <= 1 ? "text-red-400 bg-red-500/10 border-red-500/30"  
    : score <= 3 ? "text-yellow-400 bg-yellow-500/10 border-yellow-500/30"  
    : "text-green-400 bg-green-500/10 border-green-500/30";  
  const barColor =  
    score <= 1 ? "bg-red-500"  
    : score <= 3 ? "bg-yellow-500"  
    : "bg-green-500";  
  return { checks, score, label, tone, barColor, valid: score >= 4 };  
};  
  
const validateEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);  
  
const CreateSuperuserModal = ({ open, onClose, onCreated }) => {  
  const [form, setForm] = useState({ username: "", email: "", password: "" });  
  const [showPw, setShowPw] = useState(false);  
  const [loading, setLoading] = useState(false);  
  const [error, setError] = useState(null);  
  const [success, setSuccess] = useState(false);  
  
  const pw = evaluatePassword(form.password);  
  const emailValid = form.email === "" || validateEmail(form.email);  
  const usernameValid = form.username.length >= 3;  
  
  const canSubmit =  
    usernameValid && emailValid && form.email.length > 0 && pw.valid && !loading;  
  
  // Reset on close  
  useEffect(() => {  
    if (!open) {  
      const t = setTimeout(() => {  
        setForm({ username: "", email: "", password: "" });  
        setError(null);  
        setSuccess(false);  
        setShowPw(false);  
      }, 200);  
      return () => clearTimeout(t);  
    }  
  }, [open]);  
  
  const submit = async (e) => {  
    e.preventDefault();  
    if (!canSubmit) return;  
    setError(null);  
    setLoading(true);  
    try {  
      await api.post("/admin/create-superuser", {  
        username: form.username.trim(),  
        email: form.email.trim(),  
        password: form.password,  
      });  
      setSuccess(true);  
      setTimeout(() => onCreated?.(), 900);  
    } catch (err) {  
      setError(err.response?.data?.detail || "Failed to create admin. Please try again.");  
    } finally {  
      setLoading(false);  
    }  
  };  
  
  return (  
    <AnimatePresence>  
      {open && (  
        <motion.div  
          initial={{ opacity: 0 }}  
          animate={{ opacity: 1 }}  
          exit={{ opacity: 0 }}  
          onClick={onClose}  
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"  
        >  
          <motion.div  
            initial={{ opacity: 0, scale: 0.96, y: 8 }}  
            animate={{ opacity: 1, scale: 1, y: 0 }}  
            exit={{ opacity: 0, scale: 0.96, y: 8 }}  
            onClick={(e) => e.stopPropagation()}  
            className="bg-gray-900/95 backdrop-blur-xl border border-gray-800 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden"  
          >  
            {/* Header */}  
            <div className="flex items-center justify-between p-5 border-b border-gray-800">  
              <div className="flex items-center gap-3">  
                <div className="w-9 h-9 rounded-lg bg-purple-500/15 border border-purple-500/30 flex items-center justify-center">  
                  <Shield size={16} className="text-purple-300" />  
                </div>  
                <div>  
                  <h2 className="text-base font-bold text-white">New administrator</h2>  
                  <p className="text-[11px] text-gray-500">Grants full platform access</p>  
                </div>  
              </div>  
              <button  
                onClick={onClose}  
                disabled={loading}  
                className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.04] transition-all disabled:opacity-50"  
              >  
                <X size={16} />  
              </button>  
            </div>  
  
            {/* Success view */}  
            {success ? (  
              <div className="p-8 text-center">  
                <motion.div  
                  initial={{ scale: 0 }} animate={{ scale: 1 }}  
                  transition={{ type: "spring", damping: 12 }}  
                  className="w-14 h-14 mx-auto mb-4 bg-green-500/15 border border-green-500/30 rounded-full flex items-center justify-center"  
                >  
                  <CheckCircle size={26} className="text-green-400" />  
                </motion.div>  
                <h3 className="text-base font-bold text-white mb-1">Admin created</h3>  
                <p className="text-sm text-gray-400">  
                  <span className="text-gold font-semibold">{form.username}</span> now has administrator access.  
                </p>  
              </div>  
            ) : (  
              <form onSubmit={submit} className="p-5 space-y-4">  
                {/* Username */}  
                <div>  
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">  
                    Username  
                  </label>  
                  <input  
                    type="text"  
                    value={form.username}  
                    onChange={(e) => setForm({ ...form, username: e.target.value })}  
                    placeholder="e.g. jane_admin"  
                    disabled={loading}  
                    autoComplete="off"  
                    className="w-full px-3 py-2.5 bg-black/40 border border-gray-800 focus:border-gold/50 rounded-lg text-white text-sm placeholder-gray-600 outline-none transition-all disabled:opacity-50"  
                  />  
                  {form.username && !usernameValid && (  
                    <p className="text-[11px] text-red-400 mt-1.5 flex items-center gap-1">  
                      <AlertCircle size={11} /> Must be at least 3 characters  
                    </p>  
                  )}  
                </div>  
  
                {/* Email */}  
                <div>  
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">  
                    Email  
                  </label>  
                  <input  
                    type="email"  
                    value={form.email}  
                    onChange={(e) => setForm({ ...form, email: e.target.value })}  
                    placeholder="admin@company.com"  
                    disabled={loading}  
                    autoComplete="off"  
                    className="w-full px-3 py-2.5 bg-black/40 border border-gray-800 focus:border-gold/50 rounded-lg text-white text-sm placeholder-gray-600 outline-none transition-all disabled:opacity-50"  
                  />  
                  {form.email && !emailValid && (  
                    <p className="text-[11px] text-red-400 mt-1.5 flex items-center gap-1">  
                      <AlertCircle size={11} /> Invalid email address  
                    </p>  
                  )}  
                </div>  
  
                {/* Password */}  
                <div>  
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">  
                    Password  
                  </label>  
                  <div className="relative">  
                    <input  
                      type={showPw ? "text" : "password"}  
                      value={form.password}  
                      onChange={(e) => setForm({ ...form, password: e.target.value })}  
                      placeholder="At least 8 characters"  
                      disabled={loading}  
                      autoComplete="new-password"  
                      className="w-full px-3 py-2.5 pr-10 bg-black/40 border border-gray-800 focus:border-gold/50 rounded-lg text-white text-sm placeholder-gray-600 outline-none transition-all disabled:opacity-50 font-mono"  
                    />  
                    <button  
                      type="button"  
                      onClick={() => setShowPw(!showPw)}  
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-white transition-colors"  
                    >  
                      {showPw ? <EyeOff size={14} /> : <Eye size={14} />}  
                    </button>  
                  </div>  
  
                  {/* Strength */}  
                  {form.password && (  
                    <div className="mt-2 space-y-2">  
                      <div className="flex items-center justify-between gap-2">  
                        <div className="flex-1 h-1 bg-gray-800 rounded-full overflow-hidden">  
                          <motion.div  
                            initial={{ width: 0 }}  
                            animate={{ width: `${(pw.score / 5) * 100}%` }}  
                            transition={{ duration: 0.3 }}  
                            className={`h-full ${pw.barColor}`}  
                          />  
                        </div>  
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${pw.tone}`}>  
                          {pw.label}  
                        </span>  
                      </div>  
                      <div className="grid grid-cols-2 gap-1 text-[10px]">  
                        <PwCheck ok={pw.checks.length} label="8+ characters" />  
                        <PwCheck ok={pw.checks.upper} label="Uppercase" />  
                        <PwCheck ok={pw.checks.lower} label="Lowercase" />  
                        <PwCheck ok={pw.checks.number} label="Number" />  
                        <PwCheck ok={pw.checks.symbol} label="Symbol" />  
                      </div>  
                    </div>  
                  )}  
                </div>  
  
                {/* Error */}  
                {error && (  
                  <motion.div  
                    initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}  
                    className="flex items-start gap-2 p-3 bg-red-500/5 border border-red-500/30 rounded-lg"  
                  >  
                    <AlertCircle size={14} className="text-red-400 flex-shrink-0 mt-0.5" />  
                    <p className="text-xs text-red-300">{error}</p>  
                  </motion.div>  
                )}  
  
                {/* Warning */}  
                <div className="flex items-start gap-2 p-3 bg-yellow-500/5 border border-yellow-500/20 rounded-lg">  
                  <Shield size={14} className="text-yellow-500 flex-shrink-0 mt-0.5" />  
                  <p className="text-[11px] text-yellow-200/80 leading-relaxed">  
                    Administrators can manage all users, view trade history, and control platform bots. Only create accounts for trusted personnel.  
                  </p>  
                </div>  
  
                {/* Actions */}  
                <div className="flex items-center gap-2 pt-1">  
                  <button  
                    type="button"  
                    onClick={onClose}  
                    disabled={loading}  
                    className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-400 hover:text-white hover:bg-white/[0.04] rounded-lg transition-all disabled:opacity-50"  
                  >  
                    Cancel  
                  </button>  
                  <button  
                    type="submit"  
                    disabled={!canSubmit}  
                    className="flex-1 px-4 py-2.5 bg-gold hover:bg-yellow-400 text-black text-sm font-bold rounded-lg transition-all shadow-md shadow-gold/20 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none flex items-center justify-center gap-2"  
                  >  
                    {loading ? (  
                      <><RefreshCw size={13} className="animate-spin" /> Creating…</>  
                    ) : (  
                      <><Shield size={13} /> Create admin</>  
                    )}  
                  </button>  
                </div>  
              </form>  
            )}  
          </motion.div>  
        </motion.div>  
      )}  
    </AnimatePresence>  
  );  
};  
  
const PwCheck = ({ ok, label }) => (  
  <div className={`flex items-center gap-1 ${ok ? "text-green-400" : "text-gray-600"}`}>  
    <CheckCircle size={9} className={ok ? "opacity-100" : "opacity-30"} />  
    <span>{label}</span>  
  </div>  
);  
  
export default CreateSuperuserModal;  